/*
  Tab UI logic shared by <tab-panel> (featured-collections section) and
  <size-selector> (PDP size region tabs). Native replacement for the
  TabPanels component that used to live in kubix.js.

  Expected DOM for both cases:
    [role="tablist"]
      <button role="tab" id="..." aria-selected="..." aria-controls="<panelId>" tabindex="...">
      ...
    <panel-wrapper-optional>
      <div role="tabpanel" id="<panelId>" aria-labelledby="<tabId>" tabindex="0">
      ...

  initTabSwitcher(host, options) options:
    bar               selector for an underline-bar element inside `host`
    barVars           if true, set --tab-bar-position/--tab-bar-width on :root
                      instead of inline left/width on the bar element
    panelWrapper      selector for the wrapper around all panels — if present,
                      its height tracks the active panel (layout stability)
    tabActiveClass    class added to the active tab (default 'button-active')
    panelActiveClass  class added to the active panel (default 'panel-active')
    onSwitch          callback(activeTab, activePanel) fired after switch
*/
function initTabSwitcher(host, options) {
  const opts = options || {};
  const tabs = host.querySelectorAll('[role="tab"]');
  if (!tabs.length) return;

  const panels = host.querySelectorAll('[role="tabpanel"]');
  const bar = opts.bar ? host.querySelector(opts.bar) : null;
  const panelWrapper = opts.panelWrapper
    ? host.querySelector(opts.panelWrapper)
    : null;
  const tabActiveClass = opts.tabActiveClass || "button-active";
  const panelActiveClass = opts.panelActiveClass || "panel-active";
  const onSwitch = typeof opts.onSwitch === "function" ? opts.onSwitch : null;

  const setBar = (tab) => {
    if (!bar) return;
    if (opts.barVars) {
      const root = document.documentElement;
      root.style.setProperty("--tab-bar-position", `${tab.offsetLeft}px`);
      root.style.setProperty("--tab-bar-width", `${tab.clientWidth}px`);
    } else {
      bar.style.left = `${tab.offsetLeft}px`;
      bar.style.width = `${tab.clientWidth}px`;
    }
  };

  // Find the active tab — prefer aria-selected (semantic), fall back to class.
  const initialActive =
    host.querySelector('[role="tab"][aria-selected="true"]') ||
    host.querySelector(`[role="tab"].${tabActiveClass}`);

  // Initial roving tabindex + bar position + wrapper height.
  tabs.forEach((t) => {
    t.setAttribute("tabindex", t === initialActive ? "0" : "-1");
  });

  if (initialActive) {
    requestAnimationFrame(() => setBar(initialActive));
    const initialPanel = host.querySelector(
      `.${panelActiveClass}[role="tabpanel"]`
    );
    if (initialPanel) {
      if (panelWrapper) {
        panelWrapper.style.height = `${initialPanel.clientHeight}px`;
      }
      // Fire onSwitch on initial mount so consumers (e.g. the size-selector
      // radio-sync) can react to whichever panel is active at first paint —
      // not only on the first user click.
      if (onSwitch) onSwitch(initialActive, initialPanel);
    }
  }

  tabs.forEach((tab, index) => {
    tab.addEventListener("click", () => {
      tabs.forEach((t) => {
        const isActive = t === tab;
        t.classList.toggle(tabActiveClass, isActive);
        t.setAttribute("aria-selected", isActive ? "true" : "false");
        t.setAttribute("tabindex", isActive ? "0" : "-1");
      });
      setBar(tab);

      const panelId = tab.getAttribute("aria-controls");
      const newPanel = panelId ? document.getElementById(panelId) : null;
      if (!newPanel) return;

      panels.forEach((p) => {
        p.classList.toggle(panelActiveClass, p === newPanel);
      });
      if (panelWrapper) panelWrapper.style.height = `${newPanel.clientHeight}px`;

      if (onSwitch) onSwitch(tab, newPanel);
    });

    // WAI-ARIA APG tab keyboard pattern: ←/↑ previous, →/↓ next (wrapping),
    // Home / End jump to first / last. Focus moves automatically activates
    // the tab (cheap operation here, so auto-activation is the friendlier UX).
    tab.addEventListener("keydown", (event) => {
      let target = index;
      switch (event.key) {
        case "ArrowLeft":
        case "ArrowUp":
          target = index === 0 ? tabs.length - 1 : index - 1;
          break;
        case "ArrowRight":
        case "ArrowDown":
          target = index === tabs.length - 1 ? 0 : index + 1;
          break;
        case "Home":
          target = 0;
          break;
        case "End":
          target = tabs.length - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      tabs[target].focus();
      tabs[target].click();
    });
  });
}

// Expose for use by other scripts (e.g. enhancing <size-selector>).
window.initTabSwitcher = initTabSwitcher;

// <tab-panel> — used by sections/featured-collections.liquid.
if (!customElements.get("tab-panel")) {
  customElements.define(
    "tab-panel",
    class TabPanel extends HTMLElement {
      connectedCallback() {
        initTabSwitcher(this, {
          bar: ".tab-panel__nav-decoration",
          barVars: true,
          panelWrapper: ".tab-panel__panels",
          tabActiveClass: "button-active",
          panelActiveClass: "panel-active",
        });
      }
    }
  );
}

// Enhance <size-selector> instances. The element itself is defined as a
// custom element by global.js (VariantSizeRadios) for the variant-radio
// behaviour; here we add only the tab-switching layer on top.
function enhanceSizeSelectors() {
  document.querySelectorAll("size-selector").forEach((host) => {
    if (host._tabSwitcherInit) return;
    host._tabSwitcherInit = true;
    initTabSwitcher(host, {
      bar: ".size-selector__nav-border",
      tabActiveClass: "active",
      panelActiveClass: "active",
      onSwitch: (_tab, newPanel) => {
        // Sync the visual "checked" radio to the equivalent option in the new
        // region panel. All region radios share the same value (e.g. "UK6/US7/EU39"),
        // so this is a UI-only sync — the form value doesn't change.
        const checked = host.querySelector('input[type="radio"]:checked');
        if (!checked) return;
        const match = newPanel.querySelector(
          `input[type="radio"][value="${CSS.escape(checked.value)}"]`
        );
        if (match && match !== checked) match.checked = true;
      },
    });
  });
}

// Card-overlay size tabs (UK / US / EU) on product cards. Multiple cards
// share their region selection — clicking US on one card switches every
// card on the page to US — preserving the legacy showSizes() global UX.
// Each card has its own tablist for keyboard nav, but the broadcast keeps
// all cards in sync.
function enhanceCardSizeTabs() {
  const cards = document.querySelectorAll('.size-tabs[role="tablist"]');
  if (!cards.length) return;

  const setRegionAcrossCards = (region) => {
    cards.forEach((cardTabs) => {
      cardTabs.querySelectorAll('[role="tab"]').forEach((tab) => {
        const isActive = tab.getAttribute("data-region") === region;
        tab.classList.toggle("active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
        tab.setAttribute("tabindex", isActive ? "0" : "-1");
      });
    });
    document
      .querySelectorAll('[role="tabpanel"][data-region]')
      .forEach((panel) => {
        const isActive = panel.getAttribute("data-region") === region;
        panel.classList.toggle("active", isActive);
        panel.hidden = !isActive;
      });
  };

  cards.forEach((cardTabs) => {
    if (cardTabs._cardTabsInit) return;
    cardTabs._cardTabsInit = true;
    const tabs = cardTabs.querySelectorAll('[role="tab"]');
    tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => {
        const region = tab.getAttribute("data-region");
        if (region) setRegionAcrossCards(region);
      });
      tab.addEventListener("keydown", (event) => {
        let target = index;
        switch (event.key) {
          case "ArrowLeft":
          case "ArrowUp":
            target = index === 0 ? tabs.length - 1 : index - 1;
            break;
          case "ArrowRight":
          case "ArrowDown":
            target = index === tabs.length - 1 ? 0 : index + 1;
            break;
          case "Home":
            target = 0;
            break;
          case "End":
            target = tabs.length - 1;
            break;
          default:
            return;
        }
        event.preventDefault();
        tabs[target].focus();
        tabs[target].click();
      });
    });
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    enhanceSizeSelectors();
    enhanceCardSizeTabs();
  });
} else {
  enhanceSizeSelectors();
  enhanceCardSizeTabs();
}
